// SEQ-192: decides which text on screen is a dollar figure, so the report
// widget can blur it in the screenshot before the reporter sees it.
//
// Claims renders money in roughly 450 places across 45 files (formatCurrency,
// formatCompactCurrency, hand built `$${n.toLocaleString()}` strings, chart
// ticks and tooltips). Marking each by hand would miss the next one someone
// adds, so the DOM sweep in seqReportPrivacyDom.ts tags whatever these rules
// match, on every render path. The widget blurs anything carrying
// data-seq-private (see seq-feedback README, "Marking sensitive fields").
//
// The rules lean towards over-blurring: a blurred non-money number costs the
// reporter nothing, an exposed rate or claim value is a real disclosure.
//
// Safe Mode is untouched. It swaps every figure for a bullet mask at the
// formatter, so masked text never matches and is left alone.

// A dollar sign next to a digit, either side, with an optional minus or bracket:
// $1,200  $ 1,200  -$5  ($5)  1,200$  $1.5M  $140K  $184.50/hr
const DOLLAR_NEAR_DIGIT = /\$\s*[-+]?\s*\d|\d\s*\$/;

// A grouped number (1,234 / 12,345.67) or a two decimal amount (184.50).
// Money shown without a symbol, such as the P&L "prefix" cells and table
// columns headed "Amount". Percentages are excluded by the lookahead.
const GROUPED_NUMBER = /(?:^|[^\d.,])\d{1,3}(?:,\d{3})+(?:\.\d+)?(?![\d,]*\s*%)/;
const TWO_DECIMAL_AMOUNT = /(?:^|[^\d.])\d+\.\d{2}(?![\d.]|\s*%)/;

// Any decimal number (184.5, 0.75) that is not a version string (2.5.0), and
// any plain integer of four or more digits that is not a calendar year.
const DECIMAL_NUMBER = /(?:^|[^\d.])\d+\.\d+(?![\d]|\.\d|\s*%)/;
const LONG_INTEGER = /(?:^|[^\d.,])(\d{4,})(?![\d,]|\.\d|\s*%)/;

function hasNonYearLongInteger(text: string): boolean {
  const m = LONG_INTEGER.exec(text);
  if (!m) return false;
  const n = Number(m[1]);
  return !(m[1].length === 4 && n >= 1900 && n <= 2100);
}

export function textLooksLikeMoney(text: string | null | undefined): boolean {
  if (!text) return false;
  if (text.length > 4000) return false; // a whole document body, not a figure
  return (
    DOLLAR_NEAR_DIGIT.test(text) ||
    GROUPED_NUMBER.test(text) ||
    TWO_DECIMAL_AMOUNT.test(text) ||
    DECIMAL_NUMBER.test(text) ||
    hasNonYearLongInteger(text)
  );
}

// Form controls hold their value in a property, not a text node, so a text
// sweep cannot see them. Numeric fields are money often enough (rates,
// contract values, retention, hire costs) that all of them are marked.
export function inputLooksLikeMoney(input: {
  type?: string | null;
  inputMode?: string | null;
  value?: string | null;
}): boolean {
  const type = (input.type ?? "").toLowerCase();
  const mode = (input.inputMode ?? "").toLowerCase();
  if (type === "number") return true;
  if (mode === "decimal" || mode === "numeric") return true;
  return textLooksLikeMoney(input.value);
}

export const SEQ_PRIVATE_ATTRIBUTE = "data-seq-private";
